import { useEffect, useState, useCallback } from "react";
import api, { apiErrorMessage } from "../lib/api";
import { STATUS_STYLE, fmtDate } from "../lib/schedule";
import { useShifts } from "../context/ShiftsContext";
import { Plus, Repeat, Check, X } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { EmptyState } from "./OpenShifts";
import { toast } from "sonner";

export default function SwapRequests({ user, isAdmin }) {
  const { shifts: SHIFTS } = useShifts();
  const [swaps, setSwaps] = useState([]);
  const [peers, setPeers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ from_date: "", from_shift: "1st", to_user_id: "", reason: "" });

  const load = useCallback(async () => {
    try {
      const [s, u] = await Promise.all([api.get("/swaps"), api.get("/users")]);
      setSwaps(s.data);
      setPeers(u.data.filter((p) => p.id !== user.id && p.role !== "admin"));
    } catch (err) { toast.error(apiErrorMessage(err)); }
  }, [user.id]);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.from_date || !form.to_user_id) return toast.error("Fill date and coworker");
    try {
      await api.post("/swaps", form);
      toast.success("Swap requested");
      setOpen(false);
      setForm({ from_date: "", from_shift: "1st", to_user_id: "", reason: "" });
      load();
    } catch (err) { toast.error(apiErrorMessage(err)); }
  };

  const respond = async (id, status) => {
    try { await api.put(`/swaps/${id}/respond`, { status }); toast.success(`Swap ${status}`); load(); }
    catch (err) { toast.error(apiErrorMessage(err)); }
  };

  const shiftLabel = (k) => (SHIFTS.find((s) => s.key === k) || SHIFTS[0]).label;

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 font-display">Shift Swaps</h1>
          <p className="text-sm text-slate-500 mt-1">Trade a shift with a coworker. They accept, then it's set.</p>
        </div>
        {!isAdmin && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="request-swap-button" className="gap-2 bg-slate-900 hover:bg-slate-800"><Plus className="h-4 w-4" />Request Swap</Button>
            </DialogTrigger>
            <DialogContent data-testid="request-swap-dialog">
              <DialogHeader><DialogTitle>Request a shift swap</DialogTitle></DialogHeader>
              <div className="space-y-4 py-2">
                <div className="space-y-2"><Label>My shift date</Label>
                  <Input type="date" value={form.from_date} onChange={(e) => setForm({ ...form, from_date: e.target.value })} data-testid="swap-date-input" /></div>
                <div className="space-y-2"><Label>My shift</Label>
                  <Select value={form.from_shift} onValueChange={(v) => setForm({ ...form, from_shift: v })}>
                    <SelectTrigger data-testid="swap-shift-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{SHIFTS.map((s) => <SelectItem key={s.key} value={s.key}>{s.label} · {s.time}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div className="space-y-2"><Label>Swap with</Label>
                  <Select value={form.to_user_id} onValueChange={(v) => setForm({ ...form, to_user_id: v })}>
                    <SelectTrigger data-testid="swap-peer-select"><SelectValue placeholder="Choose coworker" /></SelectTrigger>
                    <SelectContent>{peers.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div className="space-y-2"><Label>Reason</Label>
                  <Textarea value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="swap-reason-input" /></div>
              </div>
              <DialogFooter><Button onClick={submit} data-testid="submit-swap-button" className="bg-slate-900 hover:bg-slate-800">Send Request</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {swaps.length === 0 ? (
        <EmptyState icon={Repeat} text="No swap requests yet." />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {swaps.map((s) => {
            const canRespond = !isAdmin && s.to_user_id === user.id && s.status === "pending";
            return (
              <div key={s.id} data-testid={`swap-card-${s.id}`} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex items-start justify-between">
                  <div className="text-sm text-slate-600">
                    <span className="font-semibold text-slate-900">{s.requester_name}</span> → <span className="font-semibold text-slate-900">{s.to_user_name}</span>
                  </div>
                  <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border capitalize ${STATUS_STYLE[s.status]}`}>{s.status}</span>
                </div>
                <div className="mt-2 text-sm text-slate-700">{fmtDate(s.from_date)} · {shiftLabel(s.from_shift)}</div>
                {s.reason && <div className="mt-1 text-sm text-slate-400">{s.reason}</div>}
                {canRespond && (
                  <div className="mt-4 flex gap-2">
                    <Button size="sm" onClick={() => respond(s.id, "accepted")} data-testid={`accept-swap-${s.id}`} className="gap-1 bg-emerald-600 hover:bg-emerald-700"><Check className="h-4 w-4" />Accept</Button>
                    <Button size="sm" variant="outline" onClick={() => respond(s.id, "rejected")} data-testid={`reject-swap-${s.id}`} className="gap-1 text-rose-600"><X className="h-4 w-4" />Decline</Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
